import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { ErrorPage } from "@/components/error/ErrorPage";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";

/**
 * 404 route. The screen itself is ``ErrorPage``; this wrapper only
 * adds the router-scoped bits ``ErrorPage`` deliberately can't have
 * (it renders outside the router too) — the SEO title, path and
 * ``noindex``.
 *
 * The ``noindex`` is load-bearing. The static host answers every
 * unmatched path with ``index.html`` and a 200, so this screen is a
 * soft 404: without the directive a crawler files each typo'd URL as
 * a real, thin, near-duplicate page. ``follow`` stays on so the
 * links out of the error screen are still crawled. On the platform
 * host the edge middleware also sends a real 404 (KAN-1955); tenant
 * hosts still rely on this tag.
 */
export default function NotFound(): JSX.Element {
  const { t } = useTranslation();
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const { pathname } = useLocation();
  useSeo({
    title: t("notFound.title"),
    path: pathname.replace(new RegExp(`^/${lang}/?`), "/"),
    lang,
    robots: "noindex, follow",
  });
  return <ErrorPage kind="not_found" />;
}
